import { Box, IconButton, Stack, useTheme } from "@mui/material";
import type { NodeViewProps } from "@tiptap/react";
import { NodeViewWrapper } from "@tiptap/react";
import { CodeIcon, DeleteIcon } from "components";
import DOMPurify from "dompurify";
import { useCallback, useState } from "react";
import { HtmlCodeBlockModal } from "./HtmlCodeBlockModal";

/**
 * React NodeView for the HtmlCodeBlock extension.
 * Shows a DOMPurify-sanitized preview of the HTML code.
 * Click opens the edit modal. Hover reveals action buttons.
 */
export const HtmlCodeBlockView = ({
  node,
  updateAttributes,
  deleteNode,
  editor,
}: NodeViewProps): JSX.Element => {
  const theme = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const code = (node.attrs.code as string) || "";

  const handleSave = useCallback(
    (updatedCode: string) => {
      updateAttributes({ code: updatedCode });
      setIsModalOpen(false);
    },
    [updateAttributes],
  );

  const handleDelete = useCallback(() => {
    deleteNode();
    setIsModalOpen(false);
  }, [deleteNode]);

  const handleClick = useCallback(() => {
    if (editor.isEditable) {
      setIsModalOpen(true);
    }
  }, [editor.isEditable]);

  const sanitizedHtml = DOMPurify.sanitize(code, {
    ADD_ATTR: ["data-value", "data-label", "data-id", "data-type", "data-code"],
  });

  return (
    <NodeViewWrapper>
      <Box
        contentEditable={false}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          position: "relative",
          border: `1px solid ${theme.palette.neutral["015"]}`,
          borderRadius: "4px",
          padding: "8px",
          margin: "4px 0",
          backgroundColor: theme.palette.neutral["005"],
          cursor: editor.isEditable ? "pointer" : "default",
          transition: "all 0.2s ease",
          "&:hover": editor.isEditable
            ? {
                backgroundColor: theme.palette.neutral["010"],
              }
            : {},
        }}
      >
        {/* Action buttons on hover */}
        {isHovered && editor.isEditable && (
          <Stack
            direction="row"
            sx={{
              position: "absolute",
              top: "4px",
              right: "4px",
              gap: "4px",
              zIndex: 1,
            }}
          >
            <IconButton
              size="small"
              aria-label="Edit HTML code block"
              onClick={(e) => {
                e.stopPropagation();
                setIsModalOpen(true);
              }}
              sx={{
                backgroundColor: theme.palette.neutral.main,
                border: `1px solid ${theme.palette.neutral["015"]}`,
                borderRadius: "4px",
                width: "24px",
                height: "24px",
                "&:hover": {
                  backgroundColor: theme.palette.neutral["010"],
                },
              }}
            >
              <CodeIcon
                sx={{
                  width: 14,
                  height: 14,
                  color: theme.palette.lightBg.medium,
                }}
              />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Delete HTML code block"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete();
              }}
              sx={{
                backgroundColor: theme.palette.neutral.main,
                border: `1px solid ${theme.palette.neutral["015"]}`,
                borderRadius: "4px",
                width: "24px",
                height: "24px",
                "&:hover": {
                  backgroundColor: theme.palette.error.tint,
                },
              }}
            >
              <DeleteIcon
                sx={{
                  width: 14,
                  height: 14,
                  color: theme.palette.lightBg.medium,
                }}
              />
            </IconButton>
          </Stack>
        )}

        {/* HTML preview */}
        {code ? (
          <Box
            // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            sx={{ pointerEvents: "none" }}
          />
        ) : (
          <Box
            sx={{
              color: theme.palette.lightBg.low,
              ...theme.typography["body-small"],
              fontStyle: "italic",
            }}
          >
            Empty HTML code block -- click to edit
          </Box>
        )}
      </Box>

      {/* Edit modal */}
      {isModalOpen && (
        <HtmlCodeBlockModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          code={code}
          onSave={handleSave}
          onDelete={handleDelete}
        />
      )}
    </NodeViewWrapper>
  );
};
